<template>
</template>

<script>
import { mapState } from 'vuex';
import { ensureGtmLoaded, pushEcommerceEvent, enrichItem } from 'assets/js/gtm';

// begin_checkout の重複送信防止に使用するセッションキー
const BEGIN_CHECKOUT_FINGERPRINT_KEY = 'gtm_begin_checkout_fingerprint';
const CART_PAGE_NO = '16';
const ORDER_COMPLETE_PAGE_NO = '23';

export default {
    name: "PT503GTMbeginCheckout",
    data() {
        return {
            eventSent: false,
        };
    },
    computed: {
        ...mapState('sh060', ['sh060Init']),
        // 決済画面に表示中のカート商品
        cartItems() {
            return this.sh060Init && Array.isArray(this.sh060Init.cartInfo) ? this.sh060Init.cartInfo : [];
        },
        // SH060 初期化データで渡される GTM コンテナ ID
        gtmContainerId() {
            return this.sh060Init && this.sh060Init.gtmcontainerId ? this.sh060Init.gtmcontainerId : '';
        },
        // GTM コンテナ ID とカート商品の両方が準備完了した場合のみ送信可能
        eventPayloadReady() {
            return !!this.gtmContainerId && this.cartItems.length > 0;
        }
    },
    watch: {
        eventPayloadReady: {
            handler(ready) {
                if (ready) {
                    this.$nextTick(() => {
                        this.initAndPushBeginCheckout();
                    })
                }
            },
            immediate: true,
        }
    },
    mounted() {
        this.$nextTick(() => {
            // ルートフックを登録し、「カートへ戻る」または「注文完了」時に重複判定指紋をクリア
            if (this.canRunInContext()) {
                this.ensureBeginCheckoutFingerprintResetHook();
            }
            this.initAndPushBeginCheckout();
        });
    },
    methods: {
        hasCheckoutRoot() {
            return !!document.querySelector('#sh060');
        },
        canRunInContext() {
            return this.hasCheckoutRoot();
        },
        initAndPushBeginCheckout() {
            if (!this.eventPayloadReady || !this.canRunInContext()) {
                return;
            }
            this.$store.commit('setGtmId', this.gtmContainerId);
            ensureGtmLoaded();
            setTimeout(() => {
                this.pushBeginCheckout();
            }, 200);
        },
        isCartRoute(route) {
            const pageNo = route && route.query ? String(route.query.pageNo || '') : '';
            return pageNo === CART_PAGE_NO;
        },
        isOrderCompleteRoute(route) {
            const pageNo = route && route.query ? String(route.query.pageNo || '') : '';
            return pageNo === ORDER_COMPLETE_PAGE_NO;
        },
        // begin_checkout の重複判定指紋を削除し、次回フローで再計測可能にする
        clearBeginCheckoutFingerprint() {
            try {
                sessionStorage.removeItem(BEGIN_CHECKOUT_FINGERPRINT_KEY);
            } catch (e) {
            }
        },
        ensureBeginCheckoutFingerprintResetHook() {
            if (this.$router.__gtmBeginCheckoutFingerprintResetHookInstalled) {
                return;
            }
            this.$router.__gtmBeginCheckoutFingerprintResetHookInstalled = true;
            this.$router.afterEach((to) => {
                // カート画面または注文完了画面に到達したら指紋をクリア:
                // 1) カートへ戻って再度決済に進む際に begin_checkout を再送信できるようにする
                // 2) 注文完了後に今回フローの重複状態をリセットする
                if (this.isCartRoute(to) || this.isOrderCompleteRoute(to)) {
                    this.clearBeginCheckoutFingerprint();
                }
            });
        },
        // 前回送信済みのカート指紋を取得
        getBeginCheckoutFingerprint() {
            try {
                return sessionStorage.getItem(BEGIN_CHECKOUT_FINGERPRINT_KEY) || '';
            } catch (e) {
                return '';
            }
        },
        // 今回送信したカート指紋を保存
        setBeginCheckoutFingerprint(fingerprint) {
            if (!fingerprint) {
                return;
            }
            try {
                sessionStorage.setItem(BEGIN_CHECKOUT_FINGERPRINT_KEY, fingerprint);
            } catch (e) {
            }
        },
        // カート指紋を生成: 商品集合 + 合計金額
        // 配送/支払い情報を変更して決済画面へ戻った際の重複送信を防止
        buildBeginCheckoutFingerprint(items = [], value = 0) {
            const normalized = items
                .map(item => `${item.item_id}:${item.quantity}:${item.price}`)
                .sort()
                .join('|');
            return `${normalized}#${value}`;
        },
        // SH060 のカート項目を GA4 begin_checkout item 形式へマッピング
        buildCheckoutItem(item) {
            return {
                item_id: item.ITEM_NO,
                item_name: item.WEB_ITEM_NM,
                quantity: Number(item.QTY),
                price: Number(item.DISP_TNK),//税抜き価格
                ...(enrichItem(item.ITEM_NO) ? enrichItem(item.ITEM_NO) : {})
            };
        },
        pushBeginCheckout() {
            if (this.eventSent || !this.eventPayloadReady || !this.canRunInContext()) {
                return;
            }
            const cartItems = Array.isArray(this.cartItems) ? this.cartItems : [];
            if (cartItems.length === 0) {
                return;
            }
            // GA4 items を組み立て
            const items = cartItems.map(item => this.buildCheckoutItem(item));
            const value = items.reduce((total, item) => {
                const subtotal = Number(item.price) * item.quantity;
                return total + subtotal;
            }, 0);

            // 重複防止: 同一指紋は再送信しない
            const fingerprint = this.buildBeginCheckoutFingerprint(items, value);
            const lastFingerprint = this.getBeginCheckoutFingerprint();
            if (fingerprint && fingerprint === lastFingerprint) {
                return;
            }

            // begin_checkout を送信
            pushEcommerceEvent('begin_checkout', {
                currency: 'JPY',
                value,
                items,
            });
            // 送信成功後に指紋を記録
            this.setBeginCheckoutFingerprint(fingerprint);
            this.eventSent = true;
        }
    }
}
</script>
